
import * as React from 'react';
import classnames from 'classnames';

import { Link } from 'libs/router';

import CroppedProfileImage from 'components/cropped-profile-image/CroppedProfileImage';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import NotificationBadge from 'components/notification-badge/NotificationBadge';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import { getEntityFromUser } from 'utils/view';

import './brand-card.scss';

import type { BrandProfileAgency, BrandProfileMember } from 'types';

applyTranslations(require('./brand-card.i18n.json'));

const statuses = ['preparation', 'active', 'inactive'];
const MAX_VISIBLE_MEMBERS_COUNT = 8;

type Props = {
  brand: BrandProfileAgency,
  isDisabled: boolean,
  onMembersClick: (event: React.SyntheticEvent<HTMLDivElement>) => void,
  onCampaignClick: (event: React.SyntheticEvent<HTMLDivElement>) => void
};

class BrandCard extends React.Component<Props> {
  renderPlaceholder(members: Array<BrandProfileMember>, isDisabled: boolean): React.ReactNode {
    return members.length > MAX_VISIBLE_MEMBERS_COUNT && (
      <SVGDynamicIcon
        name="three-dots-inside-circle-custom-fg"
        customColor="grey"
        disabled={isDisabled}
        clickable
        width={32}
        height={32}
      />
    );
  }

  renderMessageIcon(): React.ReactNode {
    const { unreadConversationsTotal } = this.props.brand;

    return (
      <div className="brand-card__message-icon">
        <SVGDynamicIcon
          name="speech-bubble-custom-bg"
          customColor="grey"
          width={22}
          height={22}
        />
        {
          unreadConversationsTotal > 0 && (
            <div className="brand-card__message-badge">
              <NotificationBadge
                value={unreadConversationsTotal}
                size="small"
              />
            </div>
          )
        }
      </div>
    );
  }

  renderMember(member: BrandProfileMember): React.ReactNode {
    const entity = getEntityFromUser(member);

    return (
      <Tooltip
        message={entity.displayName}
        width="short"
        display="flex"
        key={member.id}
      >
        <EntityAvatar
          bordered
          entity={entity}
        />
      </Tooltip>
    );
  }

  renderMembersSection(): React.ReactNode {
    const { brand, isDisabled, onMembersClick } = this.props;
    const { members } = brand;

    return (
      <div
        className="brand-card__members"
        onClick={isDisabled ? undefined : onMembersClick}
        data-arg={brand.organizationId}
      >
        <div className="brand-card__subtitle">
          { __t('brand_card.member_title', { count: members.length })}
        </div>
        <div className="brand-card__members-list">
          { members.slice(0, MAX_VISIBLE_MEMBERS_COUNT).map(this.renderMember) }
          { this.renderPlaceholder(members, isDisabled) }
        </div>
      </div>
    );
  }

  renderCampaignsSection(): React.ReactNode {
    const { brand } = this.props;

    return (
      <div className="brand-card__campaigns">
        <div className="brand-card__subtitle">
          { __t('brand_card.campaigns_title')}
        </div>
        <div className="brand-card__campaigns-content">
          {
            statuses.map(status => {
              const classes = classnames('brand-card__campaign', {
                [`brand-card__campaign-${status}`]: true
              });

              return (
                <div
                  className={classes}
                  key={status}
                >
                  <div className="brand-card__campaign-title">
                    {brand.campaignsOverview[`${status}Total`]}
                  </div>
                  { __t(`brand_card.count.${status}`) }
                </div>
              );
            })
          }
        </div>
      </div>
    );
  }

  renderCard(): React.ReactNode {
    const { brand, isDisabled, onCampaignClick } = this.props;

    const classes = classnames('brand-card', {
      'brand-card--disabled': isDisabled
    });

    const coverClasses = classnames('brand-card__cover', {
      'brand-card__cover--disabled': isDisabled
    });

    return (
      <div className={classes}>
        <Link
          onClick={isDisabled ? null : onCampaignClick}
          data-arg={brand.organizationId}
        >
          <div className={coverClasses}>
            <CroppedProfileImage
              image={brand.coverImage}
              type="cover-small"
              canEdit={false}
            />
            <div className="brand-card__profile-image">
              <CroppedProfileImage
                image={brand.profileImage}
                type="profile-small"
                canEdit={false}
              />
            </div>
          </div>
          <div className="brand-card__content">
            { this.renderMessageIcon() }
            <div className="brand-card__title">
              <h2 className="brand-card__name text-overflow-ellipsis">
                {brand.name}
              </h2>
            </div>
            { this.renderMembersSection() }
            { this.renderCampaignsSection() }
          </div>
        </Link>
      </div>
    );
  }

  render() {
    const { isDisabled } = this.props;

    if (isDisabled) {
      return (
        <Tooltip
          message={__t('brand_card.not_a_member')}
          width="long"
          display="block"
          tooltipDirection={{ vertical: 'top', horizontal: 'center' }}
        >
          {this.renderCard()}
        </Tooltip>
      );
    }

    return this.renderCard();
  }
}

export default BrandCard;
